import React from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import FireworksAnimation from './FireworksAnimation';

const Hero: React.FC = () => {
  const { elementRef: heroRef, isVisible } = useScrollAnimation({ threshold: 0.3 });

  const ArrowRightIcon = () => (
    <svg className="w-5 h-5 ml-2 transition-transform duration-200 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
    </svg>
  );

  const SparkleIcon = () => (
    <svg className="w-6 h-6 text-accent-500" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
    </svg>
  );

  return (
    <section 
      ref={heroRef}
      className="relative bg-gradient-to-br from-neutral-50 via-white to-neutral-100 overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/4 left-1/4 w-32 h-32 rounded-full bg-accent-500 blur-3xl animate-float"></div>
        <div className="absolute bottom-1/4 right-1/4 w-48 h-48 rounded-full bg-accent-600 blur-3xl animate-float" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-8 sm:pt-16 sm:pb-12 lg:pt-28 lg:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-16 xl:gap-20 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <div className={`inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 bg-accent-100 border border-accent-300 rounded-full mb-3 sm:mb-6 transition-all duration-700 ${
              isVisible ? 'animate-fade-in-down' : 'opacity-0 translate-y-4'
            }`}>
              <SparkleIcon />
              <span className="ml-2 text-xs sm:text-sm font-medium text-accent-800">Premium Quality Fireworks</span>
            </div>

            {/* Main Heading */}
            <h1 className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-neutral-900 mb-3 sm:mb-6 leading-tight transition-all duration-700 delay-150 ${
              isVisible ? 'animate-fade-in-up' : 'opacity-0 translate-y-8'
            }`}>
              Light Up Your
              <span className="block bg-gradient-to-r from-accent-600 to-accent-500 bg-clip-text text-transparent">
                Celebrations
              </span>
            </h1>

            {/* Description */}
            <p className={`text-base sm:text-lg lg:text-xl text-neutral-600 mb-4 sm:mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0 transition-all duration-700 delay-300 ${
              isVisible ? 'animate-fade-in-up' : 'opacity-0 translate-y-6'
            }`}>
              Discover our premium collection of safe, certified fireworks and firecrackers. 
              From intimate gatherings to grand celebrations, we make every moment spectacular.
            </p>

            {/* Stats */}
            <div className={`grid grid-cols-3 gap-3 sm:gap-6 lg:gap-8 mb-6 sm:mb-10 transition-all duration-700 delay-450 ${
              isVisible ? 'animate-fade-in-up' : 'opacity-0 translate-y-6'
            }`}>
              <div className="text-center lg:text-left group">
                <div className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-neutral-900 mb-0.5 sm:mb-1 group-hover:animate-bounce-soft transition-all duration-300">500+</div>
                <div className="text-xs sm:text-sm text-neutral-600 leading-tight">Happy<span className="hidden sm:inline"> Customers</span></div>
              </div>
              <div className="text-center lg:text-left group">
                <div className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-neutral-900 mb-0.5 sm:mb-1 group-hover:animate-bounce-soft transition-all duration-300">100+</div>
                <div className="text-xs sm:text-sm text-neutral-600 leading-tight">Product<span className="hidden sm:inline"> Varieties</span></div>
              </div>
              <div className="text-center lg:text-left group">
                <div className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-neutral-900 mb-0.5 sm:mb-1 group-hover:animate-bounce-soft transition-all duration-300">100%</div>
                <div className="text-xs sm:text-sm text-neutral-600 leading-tight">Safe &<span className="hidden sm:inline"> Certified</span></div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className={`flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start transition-all duration-700 delay-600 ${
              isVisible ? 'animate-fade-in-up' : 'opacity-0 translate-y-6'
            }`}>
              <Link 
                to="/quick-buy"
                className="group bg-accent-600 text-white px-6 py-3 sm:px-8 sm:py-4 rounded-xl font-semibold text-base sm:text-lg hover:bg-accent-700 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-accent-500/25 flex items-center justify-center relative overflow-hidden"
                aria-label="Quick Buy"
              >
                <span className="relative z-10">Quick Buy</span>
                <ArrowRightIcon />
                <div className="absolute inset-0 bg-gradient-to-r from-accent-700 to-accent-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>
              
              <Link 
                to="/products"
                className="bg-white text-neutral-900 px-6 py-3 sm:px-8 sm:py-4 rounded-xl font-semibold text-base sm:text-lg border-2 border-neutral-200 hover:border-accent-300 hover:bg-accent-50 transition-all duration-300 hover:shadow-lg transform hover:scale-105 hover:-translate-y-1 relative overflow-hidden group inline-block"
              >
                <span className="relative z-10">View Products</span>
                <div className="absolute inset-0 bg-gradient-to-r from-accent-50 to-accent-100 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>
            </div>

            {/* Trust Indicators - Hidden on mobile to save space */}
            <div className={`hidden sm:block mt-12 pt-8 border-t border-neutral-200 transition-all duration-700 delay-750 ${
              isVisible ? 'animate-fade-in-up' : 'opacity-0 translate-y-6'
            }`}>
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6">
                <div className="flex items-center text-sm text-neutral-600 hover:text-accent-600 transition-colors duration-300 group">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2 group-hover:animate-ping"></div>
                  Safety Certified
                </div>
                <div className="flex items-center text-sm text-neutral-600 hover:text-accent-600 transition-colors duration-300 group">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2 group-hover:animate-ping"></div>
                  Fast Delivery
                </div>
                <div className="flex items-center text-sm text-neutral-600 hover:text-accent-600 transition-colors duration-300 group">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2 group-hover:animate-ping"></div>
                  Expert Support
                </div>
              </div>
            </div>
          </div>

          {/* Right Visual - Hidden on mobile to ensure CTAs are visible */}
          <div className={`hidden lg:block relative transition-all duration-700 delay-300 ${
            isVisible ? 'animate-fade-in-right' : 'opacity-0 translate-x-8'
          }`}>
            {/* Main Image Container */}
            <div className="relative z-10">
              <div className="aspect-square rounded-4xl shadow-2xl transform rotate-3 hover:rotate-0 hover:scale-105 transition-all duration-500 hover:shadow-accent-500/25 group overflow-hidden">
                <FireworksAnimation />
              </div>
            </div>

            {/* Floating Elements */}
            <div className={`absolute top-8 -left-4 z-20 transition-all duration-700 delay-900 ${
              isVisible ? 'animate-fade-in-left' : 'opacity-0 -translate-x-4'
            }`}>
              <div className="bg-white rounded-2xl shadow-lg p-4 border border-neutral-200 hover:shadow-xl hover:scale-105 transition-all duration-300 hover:border-accent-200 group">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-2 group-hover:animate-ping"></div>
                  <span className="text-sm font-medium text-neutral-900 group-hover:text-accent-600 transition-colors duration-300">Premium Quality</span>
                </div>
              </div>
            </div>

            <div className={`absolute bottom-8 -right-4 z-20 transition-all duration-700 delay-1000 ${
              isVisible ? 'animate-fade-in-right' : 'opacity-0 translate-x-4'
            }`}>
              <div className="bg-white rounded-2xl shadow-lg p-4 border border-neutral-200 hover:shadow-xl hover:scale-105 transition-all duration-300 hover:border-accent-200 group">
                <div className="flex items-center">
                  <span className="text-2xl mr-2 group-hover:animate-wiggle">⭐</span>
                  <div>
                    <div className="text-sm font-bold text-neutral-900 group-hover:text-accent-600 transition-colors duration-300">4.9/5</div>
                    <div className="text-xs text-neutral-600">Customer Rating</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Background Decoration */}
            <div className="absolute inset-0 -z-10">
              <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-accent-200 rounded-full opacity-20 animate-float"></div>
              <div className="absolute bottom-1/3 right-1/3 w-24 h-24 bg-accent-300 rounded-full opacity-30 animate-float" style={{ animationDelay: '2s' }}></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;